import { ElementType } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { IUserJson } from '@scale/shared/types/user';
import { CorpProjectGroupNode } from '@scale/shared/types/corpProjectGroup';
import { BrowserPaths } from '@scale/shared/corpSearch';
import { CommunityType } from '@scale/shared/types/Community';

export const REVIEW_LEVELS = ['-1', 'qir_auditor', '0', '1', '4', '10', '11', '12'];

export type ChartMode = 'barChart' | 'table';

export enum GenAIOpsHubPaths {
  Main = BrowserPaths.GenAIOpsHub,
  Projects = `${BrowserPaths.GenAIOpsHub}/projects`,
  Onboarding = `${BrowserPaths.GenAIOpsHub}/onboarding`,
  CustomerSetup = `${BrowserPaths.GenAIOpsHub}/customer-setup`,
  ProjectSetup = `${BrowserPaths.GenAIOpsHub}/project-setup`,
  ProjectLibrary = `${BrowserPaths.GenAIOpsHub}/project-library`,
  VerifiedCourses = `${BrowserPaths.GenAIOpsHub}/verified-courses`,
  CommunityManagement = `${BrowserPaths.GenAIOpsHub}/community-management`,
  SlaGroupSetup = `${BrowserPaths.GenAIOpsHub}/SLA-groups-setup`,
  SlaGroups = `${BrowserPaths.GenAIOpsHub}/SLA-groups`,
  PodLeadCenter = `${BrowserPaths.GenAIOpsHub}/pod-lead-center`,
  ScreeningCenter = `${BrowserPaths.GenAIOpsHub}/screening-center`,
  Ots = `${BrowserPaths.GenAIOpsHub}/ots`,
  Demos = `${BrowserPaths.GenAIOpsHub}/demos`,
  Allocation = `${BrowserPaths.GenAIOpsHub}/allocation`,
  QCCenter = `${BrowserPaths.GenAIOpsHub}/qc-center`,
  ModelExplorer = `${BrowserPaths.GenAIOpsHub}/model-explorer`,
  Compass = `${BrowserPaths.GenAIOpsHub}/compass`,
  CorpLookup = `/lookup`,
  CorpSearch = `/search`,
  CorpDCT = `/dct`,
  OpsSearch = `${BrowserPaths.GenAIOpsHub}/search/:searchId`,
  OpsSearchBase = `${BrowserPaths.GenAIOpsHub}/search`,
  Toolbox = `${BrowserPaths.GenAIOpsHub}/toolbox`,
  ToolboxEmbedded = `${BrowserPaths.GenAIOpsHub}/toolbox/embedded`,
  CourseLibrary = `${BrowserPaths.GenAIOpsHub}/course-library`,
  ExperimentPlatform = `${BrowserPaths.GenAIOpsHub}/experiment-platform`,
  NotificationCenter = `${BrowserPaths.GenAIOpsHub}/notifications`,
  TrustAndSafetyCenter = `${BrowserPaths.GenAIOpsHub}/trust-and-safety`,
  QuestionBanks = `${BrowserPaths.GenAIOpsHub}/question-banks`,
  GaiusProjectsCenter = `${BrowserPaths.GenAIOpsHub}/gaius-projects-center`,
  Webinars = `${BrowserPaths.GenAIOpsHub}/webinars`,
}

export const CHART_OPTIONS = [
  { id: 'barChart', name: 'Bar chart' },
  { id: 'table', name: 'Table' },
];

type SidebarMenuItemCommonProps = {
  path: string;
  label: string;
  description?: string;
  icon: IconName;
  filter?: ({
    user,
    projectGroupNode,
  }: {
    user: IUserJson;
    projectGroupNode?: CorpProjectGroupNode;
  }) => boolean;
  skipRumLogging?: boolean;
};

type SidebarMenuItemWithView = SidebarMenuItemCommonProps & {
  View: ElementType;
  sections?: any[];
};

export type SidebarMenuItemWithChildren = SidebarMenuItemCommonProps & {
  children: ChildSidebarMenu[];
};

type SidebarMenuItemWithTabs = SidebarMenuItemCommonProps & {
  tabs: ChildSidebarMenu[];
};

export type SidebarMenuItem =
  | SidebarMenuItemWithView
  | SidebarMenuItemWithChildren
  | SidebarMenuItemWithTabs;

export type ChildSidebarMenu =
  | (Omit<SidebarMenuItemWithView, 'icon'> & { icon?: IconName })
  | (Omit<SidebarMenuItemWithChildren, 'icon'> & { icon?: IconName })
  | (Omit<SidebarMenuItemWithTabs, 'icon'> & { icon?: IconName });

export enum MainSidebarMenu {
  Overview = 'overview',
  ProjectInformation = 'project-information',
  Linters = 'linters',
  ContributorManagement = 'contributor-management',
  Enablement = 'enablement',
  Configs = 'configs',
  Tools = 'tools',
  FDETools = 'fde-tools',
  ProjectSetup = 'project-setup',
  DataQuality = 'data-quality',
  Quality = 'quality',
  Throughput = 'throughput',
  Finance = 'finance',
  Billing = 'billing',
  DeliveryHub = 'delivery-hub',
  RemoAdmin = 'remo-admin',
  Jobs = 'jobs',
  Squads = 'squads',
}

export enum ChatTaskMenu {
  Taxonomy = 'taxonomy',
  Config = 'config',
}

export enum ThroughputMenu {
  ThroughputDebugger = 'throughput-debugger',
  PipelineSnapshot = 'pipeline-snapshot',
  PipelineEditor = 'pipeline-editor',
  PipelineTasks = 'tasks/:taskId?/:attemptId?',
  PipelineTaskActions = 'task-actions',
  PipelineBatches = 'batches',
}

export enum TaskActionsTabs {
  TaskActions = 'task-actions',
  TaskReplication = 'task-replication',
  ManageReplications = 'manage-replications',
}

export enum PassAtKTabs {
  ConfigureRun = 'configure-run',
  RunHistory = 'run-history',
}

export enum QualityMenu {
  DataQuality = 'data-quality',
  AuditGoals = 'audit-sheets',
  TaskWall = 'task-wall',
  SBQ = 'sbq',
  BenchmarkCenter = 'benchmark-center',
  Linters = 'linters',
  ContributorQuality = 'contributor-quality',
  Diversity = 'diversity',
  Reviewers = 'reviewers',
  ArchieQC = 'archie-qc',
  PassAtK = 'pass-at-k',
  Tns = 'trust-and-safety',
  MDQConfig = 'mdq-config',
  ContributorCopilot = 'contributor-copilot',
}

export type SecondarySidebarMenu = ProjectConfigMenu | ToolsMenu;

export enum ProjectConfigMenu {
  Qualifications = 'qualifications',
  AllocationAndQualifications = 'allocation-and-qualifications',
  TimersAndThrottling = 'timers-and-throttling',
  SpecializedRouting = 'specialized-routing',
  TaskReplication = 'task-replication',
  QualityImprovement = 'quality-improvement',
  QualityControl = 'quality-control',
  OffPlatform = 'off-platform',
  AutoFeedback = 'auto-feedback',
  Pay = 'pay',
  ChangeLog = 'change-log',
}

export enum ContributorManagementTabs {
  ContributorSupply = 'contributor-supply',
  ProjectCohorts = 'project-cohorts',
}

export enum DeliveryHubMenu {
  Deliveries = 'deliveries',
  DeliverySender = 'delivery-sender',
  DeliveryFormatter = 'delivery-formatter',
  OTSUploadBatches = 'ots-task-bank-jobs',
  OTSDeliveries = 'ots-deliveries',
  OTSSender = 'ots-sender',
}

export enum ToolsMenu {
  SandboxTools = 'sandbox-tools',
  CheatingDetection = 'cheating-detection',
  ContributorRebirth = 'contributor-rebirth',
  LintersEval = 'linters-eval',
  SphereWorkspaceProvisioner = 'sphere-workspace-provisioner',
  ShareTasks = 'share-tasks',
  ExplainQueue = 'explain-queue',
  ContributorDebugger = 'contributor-debugger',
  TaskActions = 'task-actions',
  ModelHealth = 'model-health',
  BulbaBugLookupEditor = 'bulba-bug-lookup-editor',
  ArchiveProject = 'archive-project',
  DeliveryNexus = 'delivery-nexus',
  S3Upload = 's3-upload',
  CustomerUpload = 'customer-upload',
  ProjectReferrals = 'project-referrals',
  S3Browser = 's3-browser',
  S3Tracking = 's3-tracking',
  ToolHealth = 'tool-health',
}

export enum TrainingMenu {
  ActivationControlPanel = 'activation-control-panel',
  Instructions = 'instructions',
  Courses = 'courses',
  WebinarSessions = 'webinar-sessions',
  TaskPreview = 'task-preview',
}

export enum FinanceTabs {
  FinanceReport = 'finance-report',
  FinanceRequests = 'finance-requests',
  StellarQualityIncentives = 'stellar-quality-incentives',
  PaySandbox = 'pay-sandbox',
  CostCenter = 'cost-center',
  PausedHistory = 'paused-history',
  LLMSpend = 'LLM-spend',
}

export enum DataQualityTabs {
  QCMetrics = 'qc-metrics',
  QualityMetricsReport = 'quality-metrics-report',
  AutoReviewer = 'auto-reviewer',
  CheatingAndSpam = 'cheating-and-spam',
  DesignationConfigs = 'designation-configs',
  GradingConfigs = 'grading-configs',
}

export enum AuditGoalTabs {
  AuditGoals = 'sheets',
  Auditors = 'auditors',
  CompletedAudits = 'completed-audits',
  ChatBulkAudit = 'chat-bulk-audit',
  ErrorCategories = 'chat-bulk-audit-error-categories',
  AuditRubric = 'audit-rubric',
}

export enum BenchmarkCenterTabs {
  ProjectBenchmarks = 'project-benchmarks',
  BenchmarkWall = 'benchmark-wall',
  ArchieBenchmark = 'archie-benchmark',
  ContributorMetrics = 'contributor-metrics',
  AddBenchmarks = 'add-benchmarks',
  Config = 'configs',
}

export enum ArchieQCTabs {
  Setup = 'setup',
  VersionHistory = 'version-history',
  Evaluation = 'evaluation',
  Backfill = 'backfill',
  Results = 'results',
}

export enum TnsTabs {
  Setup = 'setup',
  Results = 'results',
  Backfill = 'backfill',
}

export enum ThroughputTabs {
  Forecast = 'forecast',
}

export enum PipelineTabs {
  Editor = 'editor',
  Configs = 'configs',
  TaskPriority = 'task-priority',
  RoutingHistory = 'routing-history',
  ConfigBuilder = 'config-builder',
}

export enum TimersAndThrottlingTabs {
  Timers = 'timers',
  Throttles = 'throttles',
  ExpectedHandlingTime = 'expected-handling-time',
}

export enum PayTabs {
  PayConfigs = 'pay-configs',
  StrataConfigs = 'strata-configs',
  PayPerTask = 'pay-per-task',
}

export enum TaskLookupTabs {
  Response = 'view-response',
  Properties = 'property-overview',
  Attempts = 'attempts-history',
  Pipeline = 'pipeline',
  JSON = 'json',
}

export enum AttemptLookupTabs {
  Response = 'view-response',
  Overview = 'property-overview',
  JSON = 'json',
}

export enum ContributorActionsTab {
  ContributorActions = 'contributor-actions',
}

export enum LookupSearchName {
  Task = 'Task',
  TaskAttempt = 'Task Attempt',
  Corp = 'Corp Lookup',
  User = 'User',
  Project = 'Project',
}

export enum SpecializedRoutingTabs {
  ContributorSpecializations = 'contributor-specializations',
  Configs = 'configs',
}

export enum ChatTaskConfigTabs {
  ChatTaskConfigs = 'chat-task-configs',
  GeneralConfigs = 'general-configs',
  ArchieRaterConfigs = 'archie-rater-configs',
}

export enum TaskPreviewTabs {
  Config = 'config',
  Metrics = 'metrics',
}

export enum AllocationAndQualificationsTabs {
  Qualifications = 'qualifications',
  Configs = 'configs',
}

export enum ActivationControlPanelTabs {
  FunnelView = 'funnel-view',
  CanvasView = 'canvas-view',
}

export enum BillingTabs {
  BillingOverview = 'billing-overview',
  BillingConfigs = 'billing-configs',
  BillingActivity = 'billing-activity',
}

export enum ReviewerTabs {
  ReviewerIdentification = 'reviewer-identification',
  ReviewerManagement = 'reviewer-management',
  ReviewerSpotterBenchmarks = 'reviewer-spotter-benchmarks',
  ActionLogs = 'action-logs',
  ContributorDeepDive = 'contributor-deep-dive',
  ReviewerDisputes = 'reviewer-disputes',
}

export enum QualityControlTabs {
  QCDesignation = 'quality-control-designation',
  GradingInstructions = 'grading-instructions',
}

export enum PerformanceViews {
  SuspiciousContributors = 'suspicious-contributors',
  CheatingFlaggedTasks = 'cheating-flagged-tasks',
  TasksTouchedByConfirmedCheaters = 'tasks-touched-by-confirmed-cheaters',
  WorkersDeactivatedForCheating = 'workers-deactivated-for-cheating',
}

export enum ExplainQueueTabs {
  ExplainQueue = 'explain-queue',
  MarketplaceDebugger = 'marketplace-debugger',
}

export enum LinterTabs {
  Configurations = 'configurations',
  Backtesting = 'backtesting',
  Playground = 'playground',
}

export const STANDARD_GRAPH_HEIGHT = 'h-[280px]';

export const TOAST_CONTAINER_PROPS = {
  ID: 'ops-hub-toast-container',
  LIMIT: 2,
  DRAGGABLE: false,
};

export const COMMUNITY_MANAGEMENT = 'Community Management';

export const COMMUNITY_MANAGEMENT_SLACK_CHANNEL = '#slack-communities-eng';
export const DISABLED_COMMUNITIES = [CommunityType.Experts, CommunityType.Remotasks];

export const TRUST_AND_SAFETY_CENTER = 'Trust and Safety Center';
export const TRUST_AND_SAFETY_SLACK_CHANNEL = '#violations-external';
export const DefaultDisputeCategoryDefinitions: Record<string, string> = {
  Other: 'Other',
};
